import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import {
  LegalService,
  deleteLegalService,
  extractApiError,
  formatPrice,
  getLegalService,
  servicePriceLabel,
} from '../api/legalServices';

const PRICING_LABEL: Record<LegalService['pricingType'], string> = {
  flat: 'Flat',
  tiered: 'Tiered',
  custom: 'Custom',
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-center gap-3 py-2.5 border-b border-g100 last:border-b-0">
    <span className="text-[13px] text-g500 flex-shrink-0">{label}</span>
    <span className="text-[13px] text-ink font-semibold text-right">{children}</span>
  </div>
);

const LegalServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [service, setService] = useState<LegalService | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setService(await getLegalService(Number(id)));
    } catch (err) {
      setError(extractApiError(err, 'Failed to load service'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    if (!service) return;
    setDeleting(true);
    try {
      await deleteLegalService(service.id);
      toast(service.name + ' deleted', 'info');
      navigate('/admin/legal', { replace: true });
    } catch (err) {
      toast(extractApiError(err, 'Delete failed'), 'error');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="scale" size={28} color="var(--g400)" /> Loading service...
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="card p-6 max-w-[520px]">
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--red)' }}>
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load this service</strong>
        </div>
        <div className="text-sm text-g600 mb-4">{error || 'Service not found.'}</div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/legal')}>
            Back to legal services
          </button>
        </div>
      </div>
    );
  }

  const tiers = service.pricingType === 'tiered' && Array.isArray(service.tiers) ? service.tiers : [];
  // Tiered services show their lowest – highest tier price.
  const price = servicePriceLabel(service);

  return (
    <div className="animate-fadeIn">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <button
          className="flex items-center gap-2 text-sm font-semibold text-g600 hover:text-ink transition-colors"
          onClick={() => navigate('/admin/legal')}
        >
          <Icon name="arrow-left" size={18} /> Back to legal services
        </button>
        <div className="flex items-center gap-2">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/admin/legal?edit=${service.id}`)}
          >
            <Icon name="pencil" size={14} /> Edit
          </button>
          <button
            className="btn btn-secondary btn-sm"
            style={{ color: 'var(--red)' }}
            onClick={() => setConfirmDelete(true)}
          >
            <Icon name="x-circle" size={14} color="var(--red)" /> Delete
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
        {/* Main */}
        <div className="card p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="text-xl font-bold text-ink">{service.name}</div>
            <span className={'badge flex-shrink-0 ' + (service.isActive ? 'badge-active' : 'badge-pending')}>
              {service.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="text-[12.5px] font-bold uppercase tracking-wider text-g500 mb-2">Description</div>
          <div
            className="text-sm text-g700 leading-relaxed"
            style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {service.description || <span className="italic text-g400">No description</span>}
          </div>

          {tiers.length > 0 && (
            <>
              <div className="text-[12.5px] font-bold uppercase tracking-wider text-g500 mt-6 mb-2">
                Pricing tiers
              </div>
              <div className="flex flex-col gap-2">
                {tiers.map((t, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-start gap-4 px-4 py-3 rounded-xl bg-g50 border border-g100"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink">{t.name}</div>
                      {t.description && <div className="text-[12.5px] text-g500 mt-0.5">{t.description}</div>}
                    </div>
                    <div className="text-sm font-bold text-brand flex-shrink-0">{formatPrice(t.price ?? null)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Side */}
        <div className="card p-6">
          {price && (
            <div className="text-[28px] font-extrabold text-brand leading-tight mb-3">{price}</div>
          )}
          <InfoRow label="Pricing type">{PRICING_LABEL[service.pricingType] || service.pricingType}</InfoRow>
          {tiers.length > 0 && <InfoRow label="Tiers">{tiers.length}</InfoRow>}
          <InfoRow label="Turnaround">{service.estimatedTurnaround || '—'}</InfoRow>
          <InfoRow label="Service ID">#{service.id}</InfoRow>
          <InfoRow label="Created">{formatDateTime(service.createdAt)}</InfoRow>
          <InfoRow label="Last updated">{formatDateTime(service.updatedAt)}</InfoRow>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Service"
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Keep"
        busy={deleting}
        message={
          <>
            Are you sure you want to delete <strong style={{ color: 'var(--ink)' }}>{service.name}</strong>?
            <br />
            This cannot be undone.
          </>
        }
        onConfirm={doDelete}
        onCancel={() => {
          if (!deleting) setConfirmDelete(false);
        }}
      />
    </div>
  );
};

export default LegalServiceDetail;
